/**
 * Rows-per-page for every table in the app, and the control that walks them.
 *
 * ## What this does and does not fix
 *
 * This paginates what is **displayed**. The lists are still fetched in full by `fetchAll`, which
 * walks the server's cursor pages and keeps every row in memory so that search and sort can work
 * across the whole set rather than the visible slice — searching a page you can see is not
 * searching.
 *
 * So this bounds the DOM, not the transfer. Rendering ten rows instead of four thousand is the
 * difference between a page that responds and one that stutters on every keystroke, and it is the
 * change worth making first. Moving the *fetch* to one page at a time is the next step and a much
 * larger one: search, sort and every queue filter would have to move to the server with it, or
 * they would silently start applying to a single page.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { useSiteConfig } from "@/lib/siteConfig";

/** The values Admin → Settings offers. Mirrors PAGE_SIZE_CHOICES in backend/routers/public.py. */
export const PAGE_SIZE_CHOICES = [10, 20, 30];
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Rows per page, as configured by admin.
 *
 * Reads the shared site-config cache, so the hundred tables in this app cost one request between
 * them. An unrecognised or missing value falls back to 10 rather than showing everything — the
 * failure mode of a bad config should be a small page, not an unbounded one.
 */
export function usePageSize() {
  const cfg = useSiteConfig();
  const raw = Number(cfg?.page_size);
  return PAGE_SIZE_CHOICES.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
}

/**
 * Slice `rows` into pages and keep the current one valid.
 *
 * Returns the visible slice plus what `<Pagination>` needs. The page is clamped whenever the row
 * count changes — deleting the last item on page 5, or typing a search that leaves three matches,
 * would otherwise leave the table on a page that no longer exists and render as empty, which reads
 * exactly like "your data is gone".
 */
export function usePaged(rows, pageSize) {
  const [page, setPage] = useState(0);
  const total = rows?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const current = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () => (rows || []).slice(current * pageSize, current * pageSize + pageSize),
    [rows, current, pageSize],
  );
  return { visible, page: current, setPage, pageCount, total, pageSize };
}

/**
 * Prev / next with a position readout.
 *
 * Renders nothing for a single page: a pager under a three-row table is furniture that says only
 * that there is nothing to page through. The readout counts rows ("11–20 of 47") rather than pages,
 * because "how much is there?" is the question people actually have when they look at it.
 */
export function Pagination({ page, setPage, pageCount, total, pageSize, testid = "pager" }) {
  if (pageCount <= 1) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const btn =
    "px-2.5 py-1.5 rounded-lg border border-white/10 text-[#A0AABF] hover:text-white " +
    "hover:border-[#CBA96C]/40 disabled:opacity-30 disabled:hover:text-[#A0AABF] " +
    "disabled:hover:border-white/10 disabled:cursor-not-allowed transition-colors";
  // border-b, not border-t: this sits above the header row, so its rule separates it from the
  // table below rather than floating under the last row.
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5"
         data-testid={testid}>
      <div className="text-xs text-[#6B7280]" data-testid={`${testid}-range`}>
        {from}–{to} of {total}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={btn} data-testid={`${testid}-prev`}
                onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                aria-label="Previous page">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-[#A0AABF] tabular-nums" data-testid={`${testid}-page`}>
          {page + 1} / {pageCount}
        </span>
        <button type="button" className={btn} data-testid={`${testid}-next`}
                onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1} aria-label="Next page">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default Pagination;
